import { Fragment, useContext } from "react";
import { isNil } from "lodash";
import { useHistory } from "react-router-dom";
import { buildLink } from "common/routing";
import { formatAddressFromAddressParts } from "common/utils/address";
import {
  CertificateUpload,
  CertificateUploadStatus,
  GetDocumentUploadQuery,
} from "../../generated/graphql";
import { spacing } from "../../stitches.config";
import { AuthContext } from "../Authorization/AuthContext";
import Disclaimer from "../Common/Disclaimer";
import { EmptyState } from "../Common/EmptyState";
import { Body, Title } from "../Common/Typography";
import { LayeredMap } from "../Maps/LayeredMap/LayeredMap";
import { ZoomControl } from "../Maps/MapTools/ZoomControl";
import { MapViewportContextProvider } from "../Maps/utils/MapViewportContext";
import {
  Container,
  ExtractedAddressContainer,
  ExtractedAddressSection as ExtractedAddressSectionContainer,
  GeocodeErrorsSection as GeocodeErrorsSectionContainer,
  Header,
  LocationSection,
  ParcelLocation,
  ParcelSection,
  PropertyLocation,
  PropertyLocationSection,
} from "./__styles__/Location";

type ExtractedAddress = {
  streetAddress?: Maybe<string>;
  city?: Maybe<string>;
  state?: Maybe<string>;
  zipcode?: Maybe<string>;
};

const PropertyAssignedState = ({
  property,
}: {
  property: NonNullable<
    NonNullable<GetDocumentUploadQuery["documentUpload"]>["property"]
  >;
}) => {
  const { account } = useContext(AuthContext);
  if (!account) {
    return null;
  }
  const layers = {
    marker: {
      value: { latitude: property.latitude!, longitude: property.longitude! },
    },
    parcels: {}, // nothing provided as to not highlight the 'selected' parcel on the map
  };

  return (
    <Fragment>
      <LocationSection>
        <div>
          <PropertyLocation>{property.streetAddress}</PropertyLocation>
          <PropertyLocation>
            {formatAddressFromAddressParts({
              city: property.city,
              state: property.state,
              zipcode: property.zipcode,
            })}
          </PropertyLocation>
        </div>
        <ParcelSection>
          <ParcelLocation>
            Parcel ID: {property.parcel?.parcelId}
          </ParcelLocation>
          <ParcelLocation>
            Block number: {property.parcel?.block}
          </ParcelLocation>
          <ParcelLocation>Lot number: {property.parcel?.lot}</ParcelLocation>
        </ParcelSection>
      </LocationSection>
      <Container>
        <MapViewportContextProvider
          initialViewport={{
            zoom: 17,
            latitude: property.latitude,
            longitude: property.longitude,
          }}
        >
          <LayeredMap
            width={"100%"}
            height={"297px"}
            baseMapStyle={account?.defaultBaseMap.mapboxStyle}
            account={account}
            layers={layers}
            halfMap={true}
            bottomRightMapOverlayItems={[<ZoomControl />]}
          />
        </MapViewportContextProvider>
      </Container>
    </Fragment>
  );
};

const DocumentNeedsGeocodingState = ({
  documentUpload,
}: {
  documentUpload: NonNullable<GetDocumentUploadQuery["documentUpload"]>;
}) => {
  const history = useHistory();
  return (
    <Fragment>
      <EmptyState
        title="This file is missing a location"
        body="Add a location to view it on your map and property list"
        icon="missing-location"
        primaryButtonProps={{
          label: "Add location",
          onClick: () =>
            history.push(
              buildLink("geocodeDocument", { id: documentUpload.id }),
            ),
        }}
      />
    </Fragment>
  );
};

const ExtractionInProgressState = () => {
  return (
    <EmptyState
      icon="missing-incomplete"
      title="Elevation Certificate is processing"
      body="We're extracting details, checking for errors, and adding the location. Check back soon to see the results."
      paddingTop={spacing["3xl"].value}
    />
  );
};

const ExtractedAddressSection = ({
  extractedAddress,
}: {
  extractedAddress?: Maybe<ExtractedAddress>;
}) => {
  let extractedAddressComponent = (
    <Fragment>
      <PropertyLocation>{extractedAddress?.streetAddress}</PropertyLocation>
      <PropertyLocation>
        {formatAddressFromAddressParts({
          city: extractedAddress?.city,
          state: extractedAddress?.state,
          zipcode: extractedAddress?.zipcode,
        })}
      </PropertyLocation>
    </Fragment>
  );
  if (
    !extractedAddress ||
    (isNil(extractedAddress.streetAddress) &&
      isNil(extractedAddress.city) &&
      isNil(extractedAddress.state) &&
      isNil(extractedAddress.zipcode))
  ) {
    extractedAddressComponent = (
      <EmptyState compact title={"No address detected"} paddingTop={0} />
    );
  }

  return (
    <ExtractedAddressSectionContainer>
      <Header>Extracted address</Header>
      <ExtractedAddressContainer>
        {extractedAddressComponent}
      </ExtractedAddressContainer>
    </ExtractedAddressSectionContainer>
  );
};

const GeocodeErrorsSection = ({
  geocodeData,
}: {
  geocodeData?: Maybe<CertificateUpload["geocodeData"]>;
}) => {
  const { admin } = useContext(AuthContext);

  if (!admin) {
    return null;
  }

  const errors = geocodeData?.errors;
  if (!errors || errors.length === 0) {
    return null;
  }

  return (
    <GeocodeErrorsSectionContainer>
      <Title size="small" type="semiBold">
        Geocoding Errors (Admin Only)
      </Title>
      <ul>
        {errors.map((error, index) => (
          <li key={index}>
            <Body type="regular" size="default">
              {error.message}
            </Body>
          </li>
        ))}
      </ul>
      <Disclaimer
        message="Coordinate errors are referencing coordinates returned by geocodio via the EC address, not the EC coordinates"
        margin={false}
      />
    </GeocodeErrorsSectionContainer>
  );
};

const Location = ({
  documentUpload,
  extractedAddress,
  geocodeData,
}: {
  documentUpload: NonNullable<GetDocumentUploadQuery["documentUpload"]>;
  extractedAddress?: Maybe<ExtractedAddress>;
  geocodeData?: Maybe<CertificateUpload["geocodeData"]>;
}) => {
  if (
    documentUpload.certificateUpload?.status ===
    CertificateUploadStatus.PROCESSING
  ) {
    return <ExtractionInProgressState />;
  }

  return (
    <Fragment>
      <ExtractedAddressSection extractedAddress={extractedAddress} />
      <GeocodeErrorsSection geocodeData={geocodeData} />
      <PropertyLocationSection>
        <Header>Property location</Header>
        {documentUpload.property ? (
          <PropertyAssignedState property={documentUpload.property} />
        ) : (
          <DocumentNeedsGeocodingState documentUpload={documentUpload} />
        )}
      </PropertyLocationSection>
    </Fragment>
  );
};

export default Location;
